import { CircularProgress } from '@material-ui/core';
import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import React, { JSX, useEffect, useRef, useState } from 'react';

import {
  IEChartsSankeyLabelParams,
  IEChartsSankeyLink,
  IEChartsSankeyNode,
  ISankeyNodeConfig,
  ISankeyNodeData,
} from '~/src/dash/components/charts/Sankey/common';
import { FONT_SIZE_PIXELS, FONT_WEIGHT } from '~/src/dash/components/text/common';
import { Col, Cols, Row, Rows, spacing, Text, TKey } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';

const emptyChartStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
};

// Font size constants (in pixels)
const DEFAULT_FONT_SIZE = FONT_SIZE_PIXELS.small;
const MIN_FONT_SIZE = FONT_SIZE_PIXELS.mini;

// Calculate responsive font size based on container width
const calculateFontSize = (containerWidth: number): number => {
  const minWidth = 600;
  const maxWidth = 1200;

  if (containerWidth <= minWidth) return MIN_FONT_SIZE;
  if (containerWidth >= maxWidth) return DEFAULT_FONT_SIZE;

  // Linear interpolation between min and max
  const ratio = (containerWidth - minWidth) / (maxWidth - minWidth);
  return Math.round(MIN_FONT_SIZE + ratio * (DEFAULT_FONT_SIZE - MIN_FONT_SIZE));
};

interface IGetOptionProps {
  nodes: IEChartsSankeyNode[];
  links: IEChartsSankeyLink[];
  labelFormatter: (params: IEChartsSankeyLabelParams) => string;
  nodeTooltipFormatter: (
    nodeName: string,
    value: number,
    nodeConfigMap: Record<string, ISankeyNodeConfig>,
    nodeData?: ISankeyNodeData,
  ) => string;
  linkTooltipFormatter: (
    source: string,
    target: string,
    nodeConfigMap: Record<string, ISankeyNodeConfig>,
    value: number,
  ) => string;
  nodeConfigMap: Record<string, ISankeyNodeConfig>;
  layoutIterations?: number;
  fontSize: number;
}

// ECharts configuration with better tooltip positioning
const getOption = ({
  nodes,
  links,
  labelFormatter,
  nodeTooltipFormatter,
  linkTooltipFormatter,
  nodeConfigMap,
  layoutIterations = 0,
  fontSize,
}: IGetOptionProps): EChartsOption => ({
  tooltip: {
    trigger: 'item',
    triggerOn: 'mousemove',
    confine: true,
    renderMode: 'html',
    position: (point: number[]) => {
      return [Math.round(point[0] + 20), Math.round(point[1] + 10)];
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatter: (params: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (params.dataType === 'edge') {
        return linkTooltipFormatter(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          params.data.source as string,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          params.data.target as string,
          nodeConfigMap,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          params.data.value as number,
        );
      } else {
        return nodeTooltipFormatter(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          params.name as string,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          params.value as number,
          nodeConfigMap,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          params.data?.nodeData as ISankeyNodeData | undefined,
        );
      }
    },
    backgroundColor: publicaColors.white,
    borderColor: publicaColors.lightGray,
    borderWidth: 1,
    textStyle: {
      color: publicaColors.veryDarkGray,
      fontSize,
      fontWeight: FONT_WEIGHT.base,
      fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    },
    extraCssText:
      "max-width: none; \
      width: max-content; \
      box-shadow: 0 2px 8px rgba(0,0,0,0.15); \
      padding: 8px 12px; \
      font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; \
      font-size: 14px; \
      -webkit-font-smoothing: antialiased; \
      -moz-osx-font-smoothing: grayscale; \
      text-rendering: optimizeLegibility;",
  },
  series: [
    {
      type: 'sankey',
      emphasis: {
        focus: 'adjacency',
      },
      data: nodes,
      links,
      orient: 'horizontal',
      nodeAlign: 'left', // Respects node depths - terminals don't go to the end
      nodeWidth: 12, // Reduced for less blocky appearance
      nodeGap: 40, // Increased spacing to accommodate multi-line labels with more separation
      layoutIterations, // MUST be 0 to respect node ordering
      label: {
        formatter: labelFormatter,
        position: 'right',
        color: publicaColors.veryDarkGray,
        fontSize,
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        rich: {
          labelName: {
            color: publicaColors.veryDarkGray,
            fontSize,
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
          },
          labelValue: {
            color: publicaColors.gray,
            fontSize,
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            padding: [2, 0, 0, 0], // Add 2 pixels of top padding for spacing
          },
        },
      },
      lineStyle: {
        color: 'gradient',
        opacity: 0.4,
        curveness: 0.75, // Maximum curve for visual appeal (0-1 range)
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
  ],
});

interface IProps {
  titleTKey: TKey;
  chartHeight?: number;
  loadState: LoadingState;
  nodes: IEChartsSankeyNode[];
  links: IEChartsSankeyLink[];
  labelFormatter: (params: IEChartsSankeyLabelParams) => string;
  nodeTooltipFormatter: (
    nodeName: string,
    value: number,
    nodeConfigMap: Record<string, ISankeyNodeConfig>,
    nodeData?: ISankeyNodeData,
  ) => string;
  linkTooltipFormatter: (
    source: string,
    target: string,
    nodeConfigMap: Record<string, ISankeyNodeConfig>,
    value: number,
  ) => string;
  nodeConfigMap: Record<string, ISankeyNodeConfig>;
  layoutIterations?: number;
  customNoDataMessageTKey?: TKey;
}

export const Sankey = React.memo(
  ({
    titleTKey,
    chartHeight = 320,
    loadState,
    nodes,
    links,
    labelFormatter,
    nodeTooltipFormatter,
    linkTooltipFormatter,
    nodeConfigMap,
    layoutIterations = 0,
    customNoDataMessageTKey,
  }: IProps): JSX.Element => {
    const height = `${chartHeight}px`;
    const hasData = nodes.length > 0 && links.length > 0;
    const containerRef = useRef<HTMLDivElement>(null);
    const [fontSize, setFontSize] = useState<number>(DEFAULT_FONT_SIZE);

    // Set up resize observer to dynamically adjust font size
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          const newFontSize = calculateFontSize(width);
          setFontSize(newFontSize);
        }
      });

      resizeObserver.observe(container);

      // Initial calculation
      const initialWidth = container.getBoundingClientRect().width;
      setFontSize(calculateFontSize(initialWidth));

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    const renderContent = (): JSX.Element => {
      if (loadState === LoadingStateLoading) {
        return (
          <div style={emptyChartStyle}>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col>
                <Text tKey='LoadingQuery' />
              </Col>
              <Col>
                <CircularProgress size={14} />
              </Col>
            </Cols>
          </div>
        );
      }

      if (!hasData) {
        return (
          <div style={emptyChartStyle}>
            <Text tKey={customNoDataMessageTKey || 'NoDataToDisplay'} />
          </div>
        );
      }

      return (
        <ReactECharts
          option={getOption({
            nodes,
            links,
            labelFormatter,
            nodeTooltipFormatter,
            linkTooltipFormatter,
            nodeConfigMap,
            layoutIterations,
            fontSize,
          })}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      );
    };

    return (
      <Rows>
        <Row noPadding>
          <Text bold tKey={titleTKey} />
        </Row>
        <Row noPadding style={{ height }}>
          <div ref={containerRef} style={{ height: '100%', width: '100%' }}>
            {renderContent()}
          </div>
        </Row>
      </Rows>
    );
  },
);
